import {
  CALCULATE_DELAY,
  Field as IField,
  FieldSubtype,
  FieldType,
} from "@/core/types/types";
import TextField from "./TextField";
import NumberField from "./NumberField";
import CheckboxField from "./CheckboxField";
import { SelectFieldWithCamunda } from "./SelectField";
import DateField from "./DateField";
import TimeField from "./TimeField";
import DateTimeField from "@/core/components/DateTimeField/DateTimeField";
import DefaulValueDateTimeField from "@/core/components/DefaultValueDateTimeField/DefaultValueDateTimeField";
import FlightDelayDateTimeField from "@/components/custom/FlightDelayDateTimeField/FlightDelayDateTimeField";
import Button from "./Buttons/Button";
import { FlightPicker } from "@/APR/components/custom/FlightPicker/FlightPicker";
import { useFieldVisibility } from "@/core/hooks/useFieldVisibility/useFieldVisibility";
import { AutosuggestWrapper } from "@/APR/components/custom/Autosuggest/AutosuggestWrapper";
import Text from "./Text";
import Accordion from "@/APR/components/custom/Accordion/Accordion";
import { PassengersNumber } from "@/APR/components/custom/PassengersNumber/PassengersNumber";
import RadioSelectGroup from "../RadioSelectGroup/RadioSelectGroup";
import { FlightSummary } from "@/APR/components/custom/FlightSummary/FlightSummary";
import { CaseSummary } from "@/MIE/components/custom/CaseSummary/CaseSummary";
import NextStepSummaryComponent from "../NextStepsSummary/NextStepsSummary";
import { SigningPad, SigningPadWithCamunda } from "../SigningPad/SigningPad";
import OptionalStepStatusLink from "../OptionalStepStatusLink/OptionalStepStatusLink";
import CustomerContactInfo from "@/APR/components/custom/AdditionalPassenger/CustomerContactInfo/CustomerContactInfo";
import { FileUploadWithCamunda } from "@/core/components/FileUpload/FileUpload";
import Redirect from "@/core/components/Redirect/Redirect";
import FlightCard from "@/APR/components/custom/FlightCard/FlightCard";
import VoucherCode from "@/APR/components/custom/VoucherCode/VoucherCode";
import CustomLoader from "../Loader/CustomLoader";
import UnsubscribeAction from "@/core/components/Action/UnsubscribeAction/UnsubscribeAction";
import InfoBoxTicketRefund from "@/APR/components/custom/InfoBoxTicketRefund/InfoBoxTicketRefund";
import AlternativeTransportations from "@/APR/components/custom/AlternativeTransportation/AlternativeTransportations";
import { useMemo } from "react";
import Alert from "../Alert/Alert";
import SelectMultiLegProblem from "@/APR/components/custom/SelectMultiLegProblem/SelectMultiLegProblem";
import MultipleCheckbox from "../MultipleCheckbox/MultipleCheckbox";
import CopyTextField from "./CopyTextField";
import Telephone from "../Telephone/Telephone";

import DocumentPreviewLink from "@/core/components/Field/DocumentPreviewLink";
import AddPhoneNumber from "@/components/custom/AddPhoneNumber/AddPhoneNumber";
import CurrencyInput from "@/components/custom/CurrencyInput/CurrencyInput";
import ColumnCard from "@/components/custom/ColumnCard/ColumnCard";
import LottieAnimation from "@/core/components/LottieAnimation/LottieAnimation";
import UnsubscribeErrorAction from "@/core/components/Action/UnsubscribeErrorAction/UnsubscribeErrorAction";
import { SelectNumber } from "../../../ARB/components/custom/SelectNumber/SelectNumber";
import ReferralProgram from "@/components/custom/ReferralProgram";

export interface AutosuggestOption {
  name: string;
  iata: string;
}

export interface FieldProps {
  field: IField;
  initialValue?: any; // Field subtypes can extend this interface to define their respective expected type for initialValue
  onChange?: any;
}

const Field = ({ field, initialValue, onChange }: FieldProps) => {
  // Field may be hidden based on other input values
  const isFieldVisible = useFieldVisibility(field);

  const FieldComponent: React.FC<FieldProps> | undefined = useMemo(() => {
    return getFieldComponent(field);
  }, []);
  if (!isFieldVisible || !FieldComponent) return null;

  return (
    <FieldComponent
      field={field}
      initialValue={initialValue}
      onChange={onChange}
    />
  );
};

function getFieldComponent(field: IField) {
  // TODO: Manage custom component to appropriate folders
  // Custom components

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Autosuggest
  )
    return AutosuggestWrapper;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.FlightSelector
  )
    return FlightPicker;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.TransportLegInput
  )
    return AlternativeTransportations;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Signature
  ) {
    if (field.properties?.requiredInputVariables) {
      return SigningPadWithCamunda;
    }

    return SigningPad;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.DocumentPreviewLink
  ) {
    return DocumentPreviewLink;
  }

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Telephone
  ) {
    return Telephone;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.Redirect
  )
    return Redirect;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.Accordion
  )
    return Accordion;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.NextSteps
  )
    return NextStepSummaryComponent;

  if (
    field.type === FieldType.Number &&
    field.properties?.subtype === FieldSubtype.PassengersOutput
  )
    return PassengersNumber;

  if (
    field.type === FieldType.Number &&
    field.properties?.subtype === FieldSubtype.SelectNumber
  )
    return SelectNumber;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.CustomerContactInfo
  )
    return CustomerContactInfo;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.VoucherCode
  )
    return VoucherCode;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.FileUpload
  )
    return FileUploadWithCamunda;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.MissingSteps
  ) {
    return OptionalStepStatusLink;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.AddPhoneNumber
  )
    return AddPhoneNumber;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.FlightSummary
  )
    return FlightSummary;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.MieteCaseSummary
  ) {
    return CaseSummary;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.FlightCard
  )
    return FlightCard;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.ColumnCard
  )
    return ColumnCard;

  if (
    field.type === FieldType.Number &&
    field.properties?.subtype === FieldSubtype.CurrencyInput
  )
    return CurrencyInput;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.DateTime
  ) {
    if (field.properties?.[CALCULATE_DELAY]) {
      return FlightDelayDateTimeField;
    }
    if (field.defaultValue) {
      return DefaulValueDateTimeField;
    }

    return DateTimeField;
  }

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Loader
  ) {
    return CustomLoader;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.Unsubscribe
  ) {
    return UnsubscribeAction;
  }

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.UnsubscribeError
  ) {
    return UnsubscribeErrorAction;
  }

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.InfoBoxTicketRefund
  ) {
    return InfoBoxTicketRefund;
  }

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Alert
  )
    return Alert;
  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.SelectMultiLegProblem
  )
    return SelectMultiLegProblem;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.LottieAnimation
  )
    return LottieAnimation;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.ReferralProgram
  )
    return ReferralProgram;

  // Custom RadioSelectGroup / Basic `radio` component
  if (field.type === FieldType.Radio) return RadioSelectGroup;

  // Basic components
  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Date
  )
    return DateField;

  if (
    field.type === FieldType.TextField &&
    field.properties?.subtype === FieldSubtype.Time
  ) {
    return TimeField;
  }

  if (field.type === FieldType.Select || field.type === FieldType.TagList)
    return SelectFieldWithCamunda;

  if (field.type === FieldType.TextField) return TextField;
  if (field.type === FieldType.Number) return NumberField;
  if (field.type === FieldType.Checkbox) return CheckboxField;
  if (field.type === FieldType.Checklist) return MultipleCheckbox;

  if (
    field.type === FieldType.Text &&
    field.properties?.subtype === FieldSubtype.CopyText
  )
    return CopyTextField;

  // Text components
  if (field.type === FieldType.Text) return Text;

  // Navigation
  if (field.type === FieldType.Button) return Button;

}

export default Field;
